import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { type MetaFunction } from 'react-router'

export const handle: SEOHandle = {
	getSitemapEntries: () => [
		{ route: '/legal-notice/acceptable-use', priority: 0.7 },
	],
}

export const meta: MetaFunction = ({ location }) => {
	const title = `Acceptable Use Policy | Legal Notice | ${import.meta.env.VITE_APP_TITLE}`
	const description = `Acceptable Use policy for ${import.meta.env.VITE_APP_TITLE} users`

	const url = `https://www.trailviewr.com${location.pathname}`

	return [
		{ title },
		{ name: 'description', content: description },
		{ tagName: 'link', rel: 'canonical', href: url },

		{ property: 'og:title', content: title },
		{ property: 'og:description', content: description },
		{ property: 'og:url', content: url },

		{ name: 'twitter:title', content: title },
		{ name: 'twitter:description', content: description },
		{ name: 'twitter:url', content: url },

		{
			'script:ld+json': {
				'@context': 'https://schema.org',
				'@type': 'WebPage',
				name: title,
				description,
				url,
			},
		},
	]
}

export default function AcceptableUsePolicyRoute() {
	return (
		<main className="mx-auto max-w-4xl px-4 py-8">
			<h1 className="mb-6 text-3xl font-bold">ACCEPTABLE USE POLICY</h1>
			<p className="mb-4 font-semibold">
				Last updated <span className="font-bold">October 6, 2025</span>
			</p>
			<p className="mb-4">
				This Acceptable Use Policy ("<strong>Policy</strong>") is part of our
				Terms of Service ("<strong>Legal Terms</strong>") and should therefore
				be read alongside our main Legal Terms:{' '}
				<a
					href="/legal-notice/terms-of-service"
					className="text-blue-600 hover:underline"
				>
					https://www.trailviewr.com/legal-notice/terms-of-service
				</a>
				. If you do not agree with these Legal Terms, please refrain from using
				our Services. Your continued use of our Services implies acceptance of
				these Legal Terms.
			</p>
			<p className="mb-2">
				Please carefully review this Policy which applies to any and all:
			</p>
			<p className="mb-2">
				(a) uses of our Services (as defined in "Legal Terms")
			</p>
			<p className="mb-2">
				(b) forms, materials, consent tools, comments, posts, and all other
				content available on the Services ("<strong>Content</strong>")
			</p>
			<p className="mb-4">
				(c) material which you contribute to the Services including any upload,
				post, review, disclosure, ratings, comments, chat etc., in any forum,
				chatrooms, reviews, and to any interactive services associated with it
				("<strong>Contribution</strong>")
			</p>
			<h2 className="mt-6 mb-4 text-2xl font-bold">WHO WE ARE</h2>
			<p className="mb-4">
				We are Knight Rider Consulting, Inc., doing business as TrailViewr.com
				("<strong>Company</strong>," "<strong>we</strong>," "<strong>us</strong>
				," or "<strong>our</strong>") a company registered in Texas, United
				States at 5900 Balcones Drive # 19816, Austin, TX 78731. We operate the
				website{' '}
				<a href="/" className="text-blue-600 hover:underline">
					https://www.trailviewr.com
				</a>{' '}
				(the "<strong>Site</strong>"), as well as any other related products and
				services that refer or link to this Policy (collectively, the "
				<strong>Services</strong>").
			</p>
			<h2 className="mt-6 mb-4 text-2xl font-bold">USE OF THE SERVICES</h2>
			<p className="mb-4">
				When you use the Services, you warrant that you will comply with this
				Policy and with all applicable laws.
			</p>
			<p className="mb-2">You also acknowledge that you may not:</p>
			<ul className="mb-4 list-disc space-y-2 pl-6">
				<li>
					Systematically retrieve data or other content from the Services to
					create or compile, directly or indirectly, a collection, compilation,
					database, or directory without written permission from us.
				</li>
				<li>
					Make any unauthorized use of the Services, including collecting
					usernames and/or email addresses of users by electronic or other means
					for the purpose of sending unsolicited email, or creating user
					accounts by automated means or under false pretenses.
				</li>
				<li>
					Circumvent, disable, or otherwise interfere with security-related
					features of the Services, including features that prevent or restrict
					the use or copying of any Content or enforce limitations on the use of
					the Services and/or the Content contained therein.
				</li>
				<li>Engage in unauthorized framing of or linking to the Services.</li>
				<li>
					Trick, defraud, or mislead us and other users, especially in any
					attempt to learn sensitive account information such as user passwords.
				</li>
				<li>
					Make improper use of our Services, including our support services or
					submit false reports of abuse or misconduct.
				</li>
				<li>
					Engage in any automated use of the Services, such as using scripts to
					send comments or messages, or using any data mining, robots, or
					similar data gathering and extraction tools.
				</li>
				<li>
					Interfere with, disrupt, or create an undue burden on the Services or
					the networks or the Services connected.
				</li>
				<li>
					Attempt to impersonate another user or person or use the username of
					another user.
				</li>
				<li>
					Use any information obtained from the Services in order to harass,
					abuse, or harm another person.
				</li>
				<li>
					Use the Services as part of any effort to compete with us or otherwise
					use the Services and/or the Content for any revenue-generating
					endeavor or commercial enterprise.
				</li>
				<li>
					Decipher, decompile, disassemble, or reverse engineer any of the
					software comprising or in any way making up a part of the Services,
					except as expressly permitted by applicable law.
				</li>
				<li>
					Attempt to bypass any measures of the Services designed to prevent or
					restrict access to the Services, or any portion of the Services.
				</li>
				<li>
					Harass, annoy, intimidate, or threaten any of our employees or agents
					engaged in providing any portion of the Services to you.
				</li>
				<li>
					Delete the copyright or other proprietary rights notice from any
					Content.
				</li>
				<li>
					Copy or adapt the Services' software, including but not limited to
					Flash, PHP, HTML, JavaScript, or other code.
				</li>
				<li>
					Upload or transmit (or attempt to upload or to transmit) viruses,
					Trojan horses, or other material, including excessive use of capital
					letters (yelling) and spamming (continuous posting of repetitive
					text), that interferes with any party's uninterrupted use and
					enjoyment of the Services or modifies, impairs, disrupts, alters, or
					interferes with the use, features, functions, operation, or
					maintenance of the Services.
				</li>
				<li>
					Upload or transmit (or attempt to upload or to transmit) any material
					that acts as a passive or active information collection or
					transmission mechanism, including without limitation, clear graphics
					interchange formats ("gifs"), 1×1 pixels, web bugs, cookies, or other
					similar devices (sometimes referred to as "spyware" or "passive
					collection mechanisms" or "pcms").
				</li>
				<li>
					Except as may be the result of standard search engine or Internet
					browser usage, use, launch, develop, or distribute any automated
					system, including without limitation, any spider, robot, cheat
					utility, scraper, or offline reader that accesses the Services, or
					using or launching any unauthorized script or other software.
				</li>
				<li>
					Disparage, tarnish, or otherwise harm, in our opinion, us and/or the
					Services.
				</li>
				<li>
					Use the Services in a manner inconsistent with any applicable laws or
					regulations.
				</li>
				<li>
					Use a buying agent or purchasing agent to make purchases on the
					Services.
				</li>
				<li>Sell or otherwise transfer your profile.</li>
			</ul>
			<h2 className="mt-6 mb-4 text-2xl font-bold">Subscriptions</h2>
			<p className="mb-2">
				If you subscribe to our Services, you understand, acknowledge, and agree
				that you may not, except if expressly permitted:
			</p>
			<ul className="mb-4 list-disc space-y-2 pl-6">
				<li>
					Engage in any use, including modification, copying, redistribution,
					publication, display, performance, or retransmission, of any portions
					of any Services, other than as expressly permitted by this Policy,
					without the prior written consent of Knight Rider Consulting, Inc.,
					which consent Knight Rider Consulting, Inc. may grant or refuse in its
					sole and absolute discretion.
				</li>
				<li>
					Reconstruct or attempt to discover any source code or algorithms of
					the Services, or any portion thereof, by any means whatsoever.
				</li>
				<li>
					Provide, or otherwise make available, the Services to any third party.
				</li>
				<li>Intercept any data not intended for you.</li>
				<li>
					Damage, reveal, or alter any user's data, or any other hardware,
					software, or information relating to another person or entity.
				</li>
			</ul>
			<h2 className="mt-6 mb-4 text-2xl font-bold">
				COMMUNITY/FORUM GUIDELINES
			</h2>
			<p className="mb-4">
				<a
					href="/legal-notice/community-guidelines"
					className="text-blue-600 hover:underline"
				>
					https://www.trailviewr.com/legal-notice/community-guidelines
				</a>
			</p>
			<h2 className="mt-6 mb-4 text-2xl font-bold">CONTRIBUTIONS</h2>
			<p className="mb-2">In this Policy, the term "Contributions" means:</p>
			<ul className="mb-4 list-disc space-y-2 pl-6">
				<li>
					any data, information, software, text, code, music, scripts, sound,
					graphics, photos, videos, tags, messages, interactive features, or
					other materials that you post, share, upload, submit, or otherwise
					provide in any manner on or through to the Services; or
				</li>
				<li>
					any other content, materials, or data you provide to Knight Rider
					Consulting, Inc. or use with the Services.
				</li>
			</ul>
			<p className="mb-4">
				Some areas of the Services may allow users to upload, transmit, or post
				Contributions. We may, but are under no obligation, to review or
				moderate the Contributions made on the Services, and we expressly
				exclude our liability for any loss or damage resulting from any of our
				users' breach of this Policy. Please report any Contribution that you
				believe breaches this Policy; however, we will determine, in our sole
				discretion, whether a Contribution is indeed in breach of this Policy.
			</p>
			<p className="mb-2">You warrant that:</p>
			<ul className="mb-4 list-disc space-y-2 pl-6">
				<li>
					you are the creator and owner of or have the necessary licenses,
					rights, consents, releases, and permissions to use and to authorize
					us, the Services, and other users of the Services to use your
					Contributions in any manner contemplated by the Services and this
					Policy;
				</li>
				<li>
					all of your Contributions comply with applicable laws and are original
					and true (if they represent your opinion or facts);
				</li>
				<li>
					the creation, distribution, transmission, public display, or
					performance, and the accessing, downloading, or copying of your
					Contributions do not and will not infringe the proprietary rights,
					including but not limited to the copyright, patent, trademark, trade
					secret, or moral rights of any third party; and
				</li>
				<li>
					you have the verifiable consent, release, and/or permission of each
					and every identifiable individual person in your Contributions to use
					the name or likeness of each and every such identifiable individual
					person to enable inclusion and use of your Contributions in any manner
					contemplated by the Services and this Policy.
				</li>
			</ul>
			<p className="mb-2">
				You also agree that you will not post, transmit, or upload any (or any
				part of a) Contribution that:
			</p>
			<ul className="mb-4 list-disc space-y-2 pl-6">
				<li>
					is in breach of applicable laws, regulation, court order, contractual
					obligation, this Policy, our Legal Terms, a legal duty, or that
					promotes or facilitates fraud or illegal activities;
				</li>
				<li>
					is defamatory, obscene, offensive, hateful, insulting, intimidating,
					bullying, abusive, or threatening, to any person or group;
				</li>
				<li>is false, inaccurate, or misleading;</li>
				<li>
					includes child sexual abuse material, or violates any applicable law
					concerning child pornography or otherwise intended to protect minors;
				</li>
				<li>
					contains any material that solicits personal information from anyone
					under the age of 18 or exploits people under the age of 18 in a sexual
					or violent manner;
				</li>
				<li>
					promotes violence, advocates the violent overthrow of any government,
					or incites, encourages, or threatens physical harm against another;
				</li>
				<li>
					is obscene, lewd, lascivious, filthy, violent, harassing, libelous,
					slanderous, contains sexually explicit material, or is otherwise
					objectionable (as determined by us);
				</li>
				<li>
					is discriminatory based on race, sex, religion, nationality,
					disability, sexual orientation, or age;
				</li>
				<li>bullies, intimidates, humiliates, or insults any person;</li>
				<li>
					promotes, facilitates, or assists anyone in promoting and facilitating
					acts of terrorism;
				</li>
				<li>
					infringes, or assists anyone in infringing, a third party's
					intellectual property rights or publicity or privacy rights;
				</li>
				<li>
					is deceitful, misrepresents your identity or affiliation with any
					person and/or misleads anyone as to your relationship with us or
					implies that the Contribution was made by someone else than you;
				</li>
				<li>
					contains unsolicited or unauthorized advertising, promotional
					materials, pyramid schemes, chain letters, spam, mass mailings, or
					other forms of solicitation that has been "paid for," whether with
					monetary compensation or in kind; or
				</li>
				<li>misrepresents your identity or who the Contribution is from.</li>
			</ul>
			<h2 className="mt-6 mb-4 text-2xl font-bold">REVIEW AND RATINGS</h2>
			<p className="mb-2">
				When your Contribution is a review or rating, you also agree that:
			</p>
			<ul className="mb-4 list-disc space-y-2 pl-6">
				<li>
					you have firsthand experience with the images, videos, maps, software
					and elevation charts being reviewed;
				</li>
				<li>your Contribution is true to your experience;</li>
				<li>
					you are not affiliated with competitors if posting negative reviews
					(or linked in any way to, e.g., by being the owner or
					seller/manufacturer of, a product or service if posting positive
					reviews);
				</li>
				<li>
					you cannot make or offer any conclusions as to the legality of
					conduct;
				</li>
				<li>you cannot post any false or misleading statements; and</li>
				<li>
					you do not and will not organize a campaign encouraging others to post
					reviews, whether positive or negative.
				</li>
			</ul>
			<h2 className="mt-6 mb-4 text-2xl font-bold">
				REPORTING A BREACH OF THIS POLICY
			</h2>
			<p className="mb-4">
				We may but are under no obligation to review or moderate the
				Contributions made on the Services and we expressly exclude our
				liability for any loss or damage resulting from any of our users' breach
				of this Policy.
			</p>
			<p className="mb-2">
				If you consider that any Service, Content, or Contribution:
			</p>
			<ul className="mb-4 list-disc space-y-2 pl-6">
				<li>
					breach this Policy, please email us at report@trailviewr.com, or refer
					to the contact details at the bottom of this document to let us know
					which Service, Content, or Contribution is in breach of this Policy
					and why
				</li>
				<li>
					infringe any third-party intellectual property rights, please check
					out our Legal Terms for information about our copyright infringement
					reporting process in our main Legal Terms:{' '}
					<a
						href="/legal-notice/terms-of-service"
						className="text-blue-600 hover:underline"
					>
						https://www.trailviewr.com/legal-notice/terms-of-service
					</a>
				</li>
			</ul>
			<p className="mb-4">
				We will reasonably determine whether a Service, Content, or Contribution
				breaches this Policy.
			</p>
			<h2 className="mt-6 mb-4 text-2xl font-bold">
				CONSEQUENCES OF BREACHING THIS POLICY
			</h2>
			<p className="mb-4">
				The consequences for violating our Policy will vary depending on the
				severity of the breach and the user's history on the Services, by way of
				example:
			</p>
			<p className="mb-4">
				We may, in some cases, give you a warning and/or remove the infringing
				Contribution, however, if your breach is serious or if you continue to
				breach our Legal Terms and this Policy, we have the right to suspend or
				terminate your access to and use of our Services and, if applicable,
				disable your account. We may also notify law enforcement or issue legal
				proceedings against you when we believe that there is a genuine risk to
				an individual or a threat to public safety.
			</p>
			<p className="mb-4">
				We exclude our liability for all action we may take in response to any
				of your breaches of this Policy.
			</p>
			<h2 className="mt-6 mb-4 text-2xl font-bold">DISCLAIMER</h2>
			<p className="mb-4">
				Knight Rider Consulting, Inc. is under no obligation to monitor users'
				activities, and we disclaim any responsibility for any user's misuse of
				the Services. Knight Rider Consulting, Inc. has no responsibility for
				any user or other Content or Contribution created, maintained, stored,
				transmitted, or accessible on or through the Services, and is not
				obligated to monitor or exercise any editorial control over such
				material. If Knight Rider Consulting, Inc. becomes aware that any such
				Content or Contribution violates this Policy, Knight Rider Consulting,
				Inc. may, in addition to removing such Content or Contribution and
				blocking your account, report such breach to the police or appropriate
				regulatory authority. Unless otherwise stated in this Policy, Knight
				Rider Consulting, Inc. disclaims any obligation to any person who has
				not entered into an agreement with Knight Rider Consulting, Inc. for the
				use of the Services.
			</p>
			<h2 className="mt-6 mb-4 text-2xl font-bold">
				HOW CAN YOU CONTACT US ABOUT THIS POLICY?
			</h2>
			<p className="mb-4">
				If you have any further questions or comments or wish to report any
				problematic Content or Contribution, you may contact us by:
			</p>
			<p className="mb-4">Email: hello@trailviewr.com</p>
		</main>
	)
}
